import React, { useState } from 'react';
import { XIcon, GiftIcon } from 'lucide-react';
interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  planType: 'esencial' | 'pro' | 'academia';
}
export function UpgradeModal({
  isOpen,
  onClose,
  planType
}: UpgradeModalProps) {
  const [upgradeSelected, setUpgradeSelected] = useState(false);
  const [upsellSelected, setUpsellSelected] = useState(false);
  if (!isOpen) return null;
  // Configuración para Plan Esencial
  const esencialConfig = {
    basePrice: 19990,
    baseName: 'Plan Esencial',
    upgradePrice: 5000,
    upgradeName: 'Llevate el Sistema Completo',
    upgradeDescription: 'Incluye Tomo 5 (Síntesis & Alma) + Tomo 6 (Manual de la Astróloga) + 3 Bonos Premium completos',
    upsellPrice: 6000,
    upsellName: 'Pack 35 Tiradas Esenciales',
    upsellDescription: '35 tiradas profesionales de tarot listas para utilizar y añadir a tu catálogo de servicios',
    urls: {
      both: 'https://checkout.empezahoy.shop/cart/48160857063656:1',
      upgradeOnly: 'https://checkout.empezahoy.shop/cart/48160854671592:1',
      upsellOnly: 'https://checkout.empezahoy.shop/cart/48160854606056:1',
      none: 'https://checkout.empezahoy.shop/cart/48089967624424:1'
    },
    hasUpgrade: true
  };
  // Configuración para Plan PRO
  const proConfig = {
    basePrice: 24990,
    baseName: 'Sistema PRO',
    upgradePrice: 15000,
    upgradeName: 'Upgrade al PLAN ACADEMIA TOTAL',
    upgradeDescription: 'Incluye Curso Completo de Tarot Profesional (200+ pág.) + Licencia de Re-uso Pedagógico + Estructura de Clases',
    upsellPrice: 6000,
    upsellName: 'Pack 35 Tiradas Esenciales',
    upsellDescription: '35 tiradas profesionales de tarot listas para utilizar y añadir a tu catálogo',
    urls: {
      both: 'https://checkout.empezahoy.shop/cart/48160856473832:1',
      upgradeOnly: 'https://checkout.empezahoy.shop/cart/48160855163112:1',
      upsellOnly: 'https://checkout.empezahoy.shop/cart/48160857063656:1',
      none: 'https://checkout.empezahoy.shop/cart/48160854671592:1'
    },
    hasUpgrade: true
  };
  // Configuración para Plan Academia Total (solo upsell)
  const academiaConfig = {
    basePrice: 39990,
    baseName: 'Plan Academia Total',
    upgradePrice: 0,
    upgradeName: '',
    upgradeDescription: '',
    upsellPrice: 6000,
    upsellName: 'Pack 35 Tiradas Esenciales',
    upsellDescription: '35 tiradas profesionales de tarot listas para utilizar y añadir a tu catálogo',
    urls: {
      both: '',
      upgradeOnly: '',
      upsellOnly: 'https://checkout.empezahoy.shop/cart/48160856473832:1',
      none: 'https://checkout.empezahoy.shop/cart/48160855163112:1'
    },
    hasUpgrade: false
  };
  const config = planType === 'esencial' ? esencialConfig : planType === 'pro' ? proConfig : academiaConfig;
  const calculateTotal = () => {
    let total = config.basePrice;
    if (upgradeSelected && config.hasUpgrade) total += config.upgradePrice;
    if (upsellSelected) total += config.upsellPrice;
    return total;
  };
  const getButtonText = () => {
    if (upgradeSelected && upsellSelected && config.hasUpgrade) {
      return '👉 Comprar con mejoras';
    } else if (upgradeSelected && config.hasUpgrade) {
      return planType === 'esencial' ? '👉 Llevate el Sistema Completo' : '👉 Llevate Academia Total';
    } else if (upsellSelected) {
      return `👉 Llevate ${config.baseName} PLUS`;
    } else {
      return `👉 Continuar con ${config.baseName}`;
    }
  };
  const handleContinue = () => {
    if (upgradeSelected && upsellSelected && config.hasUpgrade) {
      window.location.href = config.urls.both;
    } else if (upgradeSelected && config.hasUpgrade) {
      window.location.href = config.urls.upgradeOnly;
    } else if (upsellSelected) {
      window.location.href = config.urls.upsellOnly;
    } else {
      window.location.href = config.urls.none;
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/60 backdrop-blur-sm" data-id="element-389">
      <div className="relative bg-white rounded-2xl max-w-xl w-full shadow-2xl" data-id="element-390">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors z-10" data-id="element-391">
          <XIcon className="w-5 h-5" data-id="element-392" />
        </button>

        {/* Header */}
        <div className="text-center pt-4 pb-3 px-4" data-id="element-393">
          <div className="w-12 h-12 bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-394">
            <GiftIcon className="w-6 h-6 text-[#D97706]" data-id="element-395" />
          </div>
          <h2 className="text-xl md:text-2xl font-black text-gray-900 mb-1" data-id="element-396">
            ¡Mejorá tu compra!
          </h2>
          <p className="text-sm text-gray-600" data-id="element-397">
            Aprovechá estas ofertas exclusivas
          </p>
        </div>

        {/* Offers */}
        <div className="px-4 pb-3 space-y-2.5" data-id="element-398">
          {/* Upgrade Option - Solo para Esencial y PRO */}
          {config.hasUpgrade && <button onClick={() => setUpgradeSelected(!upgradeSelected)} className={`w-full text-left p-3 rounded-xl border-2 transition-all ${upgradeSelected ? 'border-[#0891B2] bg-[#ECFEFF]' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`} data-id="element-399">
              <div className="flex items-start gap-2.5" data-id="element-400">
                <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${upgradeSelected ? 'border-[#0891B2] bg-[#F59E0B]' : 'border-gray-300 bg-white'}`} data-id="element-401">
                  {upgradeSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-402">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-403" />
                    </svg>}
                </div>
                <div className="flex-1" data-id="element-404">
                  <div className="flex items-center gap-1.5 mb-0.5" data-id="element-405">
                    <span className="text-base" data-id="element-406">⭐</span>
                    <h3 className="text-sm font-black text-gray-900" data-id="element-407">
                      {config.upgradeName}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-600 mb-1.5 leading-tight" data-id="element-408">
                    {config.upgradeDescription}
                  </p>
                  <div className="flex items-baseline gap-1" data-id="element-409">
                    <span className="text-lg font-black text-[#0F766E]" data-id="element-410">
                      + ${config.upgradePrice.toLocaleString('es-AR')}
                    </span>
                    <span className="text-xs text-gray-500 font-medium" data-id="element-411">
                      ARS
                    </span>
                  </div>
                </div>
              </div>
            </button>}

          {/* Upsell Option */}
          <button onClick={() => setUpsellSelected(!upsellSelected)} className={`w-full text-left p-3 rounded-xl border-2 transition-all ${upsellSelected ? 'border-[#F59E0B] bg-[#FFFBEB]' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`} data-id="element-412">
            <div className="flex items-start gap-2.5" data-id="element-413">
              <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${upsellSelected ? 'border-[#F59E0B] bg-[#F59E0B]' : 'border-gray-300 bg-white'}`} data-id="element-414">
                {upsellSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-415">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-416" />
                  </svg>}
              </div>
              <div className="flex-1" data-id="element-417">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-418">
                  <span className="text-base" data-id="element-419">
                    {planType === 'esencial' ? '📋' : '🔮'}
                  </span>
                  <h3 className="text-sm font-black text-gray-900" data-id="element-420">
                    {config.upsellName}
                  </h3>
                </div>
                <p className="text-xs text-gray-600 mb-1.5 leading-tight" data-id="element-421">
                  {config.upsellDescription}
                </p>
                <div className="flex items-baseline gap-1" data-id="element-422">
                  <span className="text-lg font-black text-[#D97706]" data-id="element-423">
                    + ${config.upsellPrice.toLocaleString('es-AR')}
                  </span>
                  <span className="text-xs text-gray-500 font-medium" data-id="element-424">ARS</span>
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* Summary */}
        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 rounded-b-2xl" data-id="element-425">
          <div className="space-y-1 mb-2.5" data-id="element-426">
            <div className="flex items-center justify-between text-gray-700 text-xs" data-id="element-427">
              <span className="font-medium" data-id="element-428">{config.baseName}</span>
              <span className="font-bold" data-id="element-429">
                ${config.basePrice.toLocaleString('es-AR')}
              </span>
            </div>
            {upgradeSelected && config.hasUpgrade && <div className="flex items-center justify-between text-[#0F766E] text-xs" data-id="element-430">
                <span className="font-medium" data-id="element-431">
                  {planType === 'esencial' ? 'Upgrade Sistema Completo' : 'Upgrade Academia Total'}
                </span>
                <span className="font-bold" data-id="element-432">
                  + ${config.upgradePrice.toLocaleString('es-AR')}
                </span>
              </div>}
            {upsellSelected && <div className="flex items-center justify-between text-[#D97706] text-xs" data-id="element-433">
                <span className="font-medium" data-id="element-434">
                  {planType === 'esencial' ? 'Tarjetas + Kit PRO' : 'Pack 35 Tiradas'}
                </span>
                <span className="font-bold" data-id="element-435">
                  + ${config.upsellPrice.toLocaleString('es-AR')}
                </span>
              </div>}
          </div>

          <div className="flex items-center justify-between mb-3 pt-2.5 border-t border-gray-300" data-id="element-436">
            <span className="text-lg font-black text-gray-900" data-id="element-437">Total</span>
            <span className="text-2xl font-black text-[#4F46E5]" data-id="element-438">
              ${calculateTotal().toLocaleString('es-AR')}
            </span>
          </div>

          {/* CTA Button */}
          <button onClick={handleContinue} className="w-full bg-gradient-to-r from-[#4F46E5] to-[#6366F1] hover:from-[#4338CA] hover:to-[#4F46E5] text-white font-black py-3 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg text-sm" data-id="element-439">
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>;
}